import React, { useState, useEffect } from 'react';
import { Check } from 'lucide-react';
import CreatorLayout from './CreatorLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const AUTH_TOKEN_KEY = 'ivisora_auth_token';

const CreatorProfile = () => {
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({ display_name: '', bio: '', available_for_hire: false });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) return;
    fetch(`${API_URL}/api/creator/profile`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d) {
          setProfile(d);
          setForm({ display_name: d.display_name || '', bio: d.bio || '', available_for_hire: d.available_for_hire || false });
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.display_name.trim()) { setError('Display name is required.'); return; }
    setSaving(true);
    setError('');
    try {
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      const r = await fetch(`${API_URL}/api/creator/profile`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          display_name: form.display_name.trim(),
          bio: form.bio,
          available_for_hire: form.available_for_hire,
        }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || 'Failed to save.');
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setError(err.message);
    }
    setSaving(false);
  };

  return (
    <CreatorLayout>
      <h1 className="text-xl font-bold text-gray-900 mb-6">Profile</h1>

      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="w-5 h-5 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="max-w-xl">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            {error && (
              <div className="mb-4 px-4 py-3 bg-red-50 border border-red-100 rounded-xl text-sm text-red-600">{error}</div>
            )}

            <form onSubmit={handleSave} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Display name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.display_name}
                  onChange={e => setForm(f => ({ ...f, display_name: e.target.value }))}
                  maxLength={60}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Bio</label>
                <textarea
                  value={form.bio}
                  onChange={e => setForm(f => ({ ...f, bio: e.target.value }))}
                  rows={4}
                  maxLength={300}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-violet-500"
                />
                <p className="text-xs text-gray-400 text-right mt-1">{form.bio.length}/300</p>
              </div>

              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.available_for_hire}
                  onChange={e => setForm(f => ({ ...f, available_for_hire: e.target.checked }))}
                  className="w-4 h-4 rounded text-violet-600 focus:ring-violet-500"
                />
                <span className="text-sm text-gray-700">Available for hire / custom work</span>
              </label>

              <div className="flex items-center gap-3 pt-2">
                <button
                  type="submit"
                  disabled={saving || !form.display_name.trim()}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 disabled:opacity-50 transition-colors"
                >
                  {saving ? 'Saving…' : saved ? <><Check className="w-4 h-4" /> Saved!</> : 'Save changes'}
                </button>
              </div>
            </form>
          </div>

          {/* Account info (read-only) */}
          {profile && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mt-5">
              <p className="font-semibold text-gray-900 mb-4">Account</p>
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Email</p>
                  <p className="text-sm text-gray-700">{profile.email}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Name</p>
                  <p className="text-sm text-gray-700">{profile.name}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </CreatorLayout>
  );
};

export default CreatorProfile;
